// Buyer "Get to know you" intake form. Fields mirror the BuyerHub reference
// schema so the dashboard progress meter and the admin submissions inbox all
// share the same shape.
import React, { useEffect, useRef, useState } from "react";
import { Field, useFormSubmit, FormFooter } from "./_shared";
import { api } from "../../lib/api";
import { useContent, DEFAULT_CONTENT } from "../../lib/content";
import { useBuyerContent } from "../../lib/buyerContent";
import { Save } from "lucide-react";

const DRAFT_ENDPOINT = "/forms/draft/buyer_intake";

export default function BuyerIntakeForm({ onSubmitted }) {
  const { buyerIntakeSections: SECTIONS } = useBuyerContent();
  const [values, setValues] = useState({});
  const [savedAt, setSavedAt] = useState(null);
  const saveTimer = useRef(null);
  const loaded = useRef(false);
  const { submit, busy, err } = useFormSubmit("buyer_intake");

  useEffect(() => {
    let alive = true;
    (async () => {
      try { const { data } = await api.get(DRAFT_ENDPOINT); if (alive) setValues(data.data || {}); }
      catch { /* no draft */ }
      finally { loaded.current = true; }
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!loaded.current) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try { await api.put(DRAFT_ENDPOINT, { data: values }); setSavedAt(new Date()); }
      catch { /* silent draft save */ }
    }, 1000);
    return () => { if (saveTimer.current) clearTimeout(saveTimer.current); };
  }, [values]);

  const upd = (k) => (e) => setValues((prev) => ({ ...prev, [k]: e.target.value }));

  return (
    <form
      onSubmit={(e) => { e.preventDefault(); submit(values, onSubmitted); }}
      className="card"
      style={{ padding: 32, display: "flex", flexDirection: "column", gap: 28 }}
      data-testid="buyer-intake-form"
    >
      <div style={{ color: "var(--text-muted)", fontSize: 14, lineHeight: 1.55, padding: "12px 16px", background: "#F0EDE3", borderRadius: 12, border: "1px solid var(--border)" }}>
        <b style={{ display: "block", marginBottom: 4, color: "var(--text)" }}>A quick intro so I can serve you well.</b>
        Most clients finish in under 3 minutes. Draft auto-saves as you type — you can come back and finish anytime.
      </div>

      {SECTIONS.map((sec, i) => (
        <section key={i} data-testid={`buyer-intake-section-${i}`}>
          <div className="overline" style={{ marginBottom: 6 }}>{String(i + 1).padStart(2, "0")} · {sec.title}</div>
          <div style={{ height: 1, width: 40, background: "var(--primary)", marginBottom: 20 }} />
          <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
            {sec.fields.map((f, fi) => (
              <Field key={`${i}-${fi}-${f.key || ""}`} label={f.label}>
                {f.type === "textarea" ? (
                  <textarea data-testid={`buyer-intake-${f.key}`} className="input-field" rows={4} value={values[f.key] || ""} onChange={upd(f.key)} placeholder={f.placeholder || ""} />
                ) : f.type === "select" ? (
                  <select data-testid={`buyer-intake-${f.key}`} className="input-field" value={values[f.key] || ""} onChange={upd(f.key)}>
                    <option value="">Select…</option>
                    {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : (
                  <input data-testid={`buyer-intake-${f.key}`} className="input-field" type={f.type} value={values[f.key] || ""} onChange={upd(f.key)} placeholder={f.placeholder || ""} />
                )}
              </Field>
            ))}
          </div>
        </section>
      ))}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <span style={{ fontSize: 12, color: "var(--text-muted)", display: "inline-flex", alignItems: "center", gap: 6 }}>
          <Save size={12} /> {savedAt ? `Draft saved ${savedAt.toLocaleTimeString()}` : "Draft saves automatically as you type"}
        </span>
      </div>

      <FormFooter busy={busy} err={err} testid="buyer-intake" />
    </form>
  );
}
